import React from "react";
import analyticsService from "../AnalyticsService";
import { isErrorTrackingEnabled } from "../constants";
import { sanitizePagePath } from "./resolvePageName";

const getReasonType = (reason) => {
  if (reason === null) return "null";
  if (reason === undefined) return "undefined";
  if (reason instanceof Error) return "error";
  return typeof reason;
};

const getErrorDetails = (error, info = {}) => ({
  error_type: error?.name || "Error",
  component: info?.component || "global",
  screen: sanitizePagePath(globalThis?.location?.pathname),
  status_code: info?.status_code || null,
  reason_type: info?.reason_type || null,
  timestamp: new Date().toISOString(),
});

export const setupGlobalErrorTracking = () => {
  if (!isErrorTrackingEnabled()) return () => {};

  const onWindowError = (event) => {
    analyticsService.trackError(
      getErrorDetails(event?.error || new Error("window_error"), {
        component: "window.onerror",
      }),
    );
  };

  const onUnhandledRejection = (event) => {
    const reason = event?.reason;
    const error =
      reason instanceof Error ? reason : new Error("promise_rejection");

    analyticsService.trackError(
      getErrorDetails(error, {
        component: "unhandledrejection",
        reason_type: getReasonType(reason),
      }),
    );
  };

  globalThis.addEventListener("error", onWindowError);
  globalThis.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    globalThis.removeEventListener("error", onWindowError);
    globalThis.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
};

export class AnalyticsErrorBoundary extends React.Component {
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error) {
    if (!isErrorTrackingEnabled()) return;

    analyticsService.trackError(
      getErrorDetails(error, { component: "react.error_boundary" }),
    );
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }

    return this.props.children;
  }
}
